import clsx from 'clsx'
import ReactDOM from "react-dom"
import React, { FC } from "react"
import { toast } from "react-toastify"
import { Dialog, DialogBackdrop, DialogPanel } from "@headlessui/react"

import { ModalProps } from '../../utils/Interfaces'
import { Loading } from "../../components/utils/Loading"
import { Thematic } from '../../components/utils/Thematic'

export const DynamicModal: FC<ModalProps> = ({
    show,
    size,
    showOrHide,
    title,
    onSubmit,
    posting,
    content,
    actionName,
    status,
    error,
    noAction = false
}) => {
    const checkIfFormIsPostingData = () => {
        if (!posting) {
            showOrHide()
            return
        }

        let toastText = 'Cannot dismiss. Current operation is still in progress'

        toast.warning(toastText, {
            position: "top-right",
            autoClose: 7000,
            hideProgressBar: true,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
        });
    }

    const sizeClasses = {
        'sm': 'md:max-w-sm',
        'md': 'md:max-w-md',
        'lg': 'md:max-w-lg',
        'xl': 'md:max-w-xl',
        '2xl': 'md:max-w-2xl',
        '3xl': 'md:max-w-3xl',
        '4xl': 'md:max-w-4xl',
        '5xl': 'md:max-w-5xl',
        '6xl': 'md:max-w-6xl',
    };

    return ReactDOM.createPortal(
        <Dialog open={show} onClose={checkIfFormIsPostingData} className="relative z-30">
            <DialogBackdrop
                transition
                className="fixed inset-0 bg-gray-500 dark:bg-gray-900/80 backdrop-blur-sm bg-opacity-65 transition-opacity data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in"
            />

            <div className="fixed inset-0 z-30 w-screen h-screen overflow-y-auto">
                <div className="flex h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
                    <DialogPanel
                        transition
                        className={clsx(
                            size && sizeClasses[size],
                            size === '6xl' ? 'sm:max-w-full sm:top-0 sm:left-0 sm:right-0 sm:bottom-0 sm:w-screen sm:h-full rounded-none' : "rounded-md",
                            "relative transform overflow-hidden bg-white dark:bg-slate-800 text-left shadow-xl transition-all data-[closed]:translate-y-4 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in sm:my-8 sm:w-full data-[closed]:sm:translate-y-0 data-[closed]:sm:scale-95")}
                    >
                        <Thematic>
                            {
                                status === 'rejected' ? (
                                    <div className="">
                                        {
                                            error ? (
                                                <>
                                                    <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                                                        <div className="flex items-center align-middle w-full gap-4">
                                                            <div className="w-14 h-14 flex-none rounded-full bg-red-100 flex items-center justify-center mx-auto">
                                                                <span className="fal fa-exclamation-triangle fa-3x -mt-3 text-red-600"></span>
                                                            </div>

                                                            <div className="flex-1 text-left flex flex-col gap-2">
                                                                <span className="text-red-600 dark:text-red-300 block text-lg md:text-lg md:font-normal font-medium">
                                                                    {error.title}
                                                                </span>

                                                                <div className="text-sm leading-7">
                                                                    {error.message}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="bg-slate-100 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                                                        <button
                                                            type="button"
                                                            onClick={checkIfFormIsPostingData}
                                                            className="w-full inline-flex justify-center text-sm rounded-md border border-transparent shadow-sm px-3 py-1 bg-red-600 text-white hover:bg-red-700 focus:outline-none sm:ml-3 sm:w-auto sm:text-sm">
                                                            Close
                                                        </button>
                                                    </div>
                                                </>
                                            ) : (
                                                <>
                                                    <div className="bg-white flex text-red-600 md:flex-row align-middle items-center flex-col px-4 pt-3 pb-4">
                                                        <div className="flex-none flex align-middle justify-center items-center md:w-24 py-3 w-full">
                                                            <span className="fa-duotone fa-cloud-exclamation fa-3x"></span>
                                                        </div>

                                                        <div className="flex-grow flex flex-col items-center">
                                                            <div className="mt-3 m-auto text-slate-600">
                                                                <span className="text-red-600 text-lg mb-2 block">
                                                                    Communication Breakdown
                                                                </span>

                                                                <div className="text-sm">
                                                                    We had some trouble processing your request. Try again later
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>

                                                    <div className="bg-slate-100 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                                                        <div className="w-12/12 space-y-px">
                                                            <div className="flex flex-row-reverse items-center align-middle">
                                                                <button type="button" className="w-full inline-flex justify-center text-sm rounded-md border-0 border-transparent shadow-none px-3 py-1 bg-inherit text-primary hover:bg-slate-200 sm:ml-3 sm:w-auto sm:text-sm" onClick={checkIfFormIsPostingData}>
                                                                    Cancel
                                                                </button>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </>
                                            )
                                        }
                                    </div>
                                ) : status === 'fulfilled' ? (
                                    <form className="rounded-md shadow-none space-y-px flex flex-col w-full h-full" onSubmit={onSubmit}>
                                        <div className={`bg-white dark:bg-slate-800 ${title ? 'pt-5 pb-2 px-6 sm:pb-2' : 'pb-2'} container md:max-w-4xl lg:max-w-4xl xl:max-w-5xl mx-auto`}>
                                            <h2 className="text-xl text-slate-800 dark:text-slate-200">
                                                {title}
                                            </h2>
                                        </div>

                                        <div className={`bg-white dark:bg-slate-800 pb-4 py-1 container md:max-w-4xl lg:max-w-4xl xl:max-w-5xl mx-auto sm:pb-4 ${size === '6xl' ? "h-full flex-1 bg-emerald-100" : "max-h-[calc(100vh-4rem)] sm:max-h-[calc(100vh-6rem)] md:max-h-[640px] md:overflow-y-auto"}`}>
                                            <div className={`w-full h-full overflow-y-auto px-4 sm:px-6`}>
                                                {
                                                    content
                                                }
                                            </div>
                                        </div>

                                        <div className="bg-slate-100 dark:bg-slate-900/40 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                                            <div className="w-full space-y-px container md:max-w-4xl lg:max-w-4xl xl:max-w-5xl mx-auto">
                                                <div className="flex flex-row-reverse items-center align-middle">
                                                    {
                                                        noAction ? (
                                                            <button type="button" className="w-24 justify-center disabled:cursor-not-allowed text-sm rounded-md border border-transparent shadow-sm px-3 py-1-5 bg-primary text-white disabled:bg-primary hover:bg-secondary focus:outline-none focus:ring-0 focus:ring-offset-2 focus:ring-primary" onClick={showOrHide}>
                                                                Done
                                                            </button>
                                                        ) : (
                                                            <>
                                                                <button type="submit" className="w-auto min-w-24 justify-center disabled:cursor-not-allowed text-sm rounded-md border border-transparent shadow-sm px-2.5 py-1.5 bg-orange-500 text-white disabled:bg-primary hover:bg-orange-600 focus:outline-none" disabled={posting}>
                                                                    {
                                                                        posting ? (
                                                                            <span className="flex h-6 items-center justify-center">
                                                                                <i className="fa-duotone fa-spinner-third fa-xl fa-spin"></i>
                                                                            </span>
                                                                        ) : (
                                                                            actionName
                                                                        )
                                                                    }
                                                                </button>

                                                                <button type="button" className="text-sm flex-none shadow-none px-3 py-1 bg-inherit text-slate-500 dark:text-slate-400 hover:text-secondary hover:underline hover:cursor-pointer mr-2 sm:w-auto sm:text-sm" onClick={checkIfFormIsPostingData}>
                                                                    Cancel
                                                                </button>
                                                            </>
                                                        )
                                                    }
                                                </div>
                                            </div>
                                        </div>
                                    </form>
                                ) : (
                                    <div className="py-2 px-6 w-full">
                                        <Loading />
                                    </div>
                                )
                            }
                        </Thematic>
                    </DialogPanel>
                </div>
            </div>
        </Dialog>,
        document.getElementById('root')
    )
}